import { Badge, Card } from "@/components/common";
import HeroBusiness from "@/components/common/HeroBusiness";
import PageTitleBox from "@/components/common/PageTitleBox";
import Container from "@/components/layout/Container";
import { Link } from "react-router-dom";

const rcsTemplates = [
  {
    image: "/img/business/rcs/business_rcs_01.png",
    title: "텍스트 템플릿-단문",
    type: "(정보성)",
  },
  {
    image: "/img/business/rcs/business_rcs_02.png",
    title: "텍스트 템플릿-장문",
    type: "(광고성)",
  },
  {
    image: "/img/business/rcs/business_rcs_03.png",
    title: "이미지 템플릿",
    type: "(광고성)",
  },
];

const purposes = [
  {
    icon: "verified_user",
    title: "신뢰도 강화",
    description: "기업 인증 바코와 브랜드 프로필을 통해 스팸/스미싱 우려를 해소",
  },
  {
    icon: "trending_up",
    title: "전환율 극대화",
    description:
      "텍스트 중심의 문자에서 벗어나 카드형 레이아웃과 액션 버튼으로 고객의 즉각적인 반응을 유도",
  },
  {
    icon: "public",
    title: "도달 범위 확장",
    description: "특정 앱 사용 여부와 상관없이 모든 스마트폰 유저를 잠재적 수신자로 확보",
  },
];

const capabilities = [
  {
    step: 1,
    title: "통합 정보성 메시지 전송",
    description:
      "안드로이드부터 최신 iOS까지 아우르는 표준규격 메시지로 중요한 고객을 빠뜨리지 않습니다.",
  },
  {
    step: 2,
    title: "유연한 발송 모드 (검수형/자유형)",
    description:
      "검수형: 고품질 레이아웃과 단순 마크업으로 구성된 신뢰 기반 템플릿 발송 / 자유형: 즉시 전송하기",
  },
  {
    step: 3,
    title: "결제 버튼을 통한 간편결제",
    description:
      "발신자 정보 확인이 가능한 브랜드 프로필 제공 및 보이스피싱 방지를 위한 공식 인증마크 노출",
  },
];

const BusinessRcs = () => {
  return (
    <Container>
      <HeroBusiness title="비즈니스를 완성하는" />

      <section id="main-content" className="py-[70px]">
        {/* Page Header */}
        <PageTitleBox
          badge="RCS"
          title={
            <>
              차세대 메시징의 새로운 표준,
              <br />
              <strong className="text-[#1976D2]">RCS</strong>
            </>
          }
          subtitle={
            <>
              RCS (Rich Communication Services)는 기존 문자 메시지를 넘어선 풍부한 커뮤니케이션
              서비스입니다.
              <br />
              이미지, 동영상, 버튼 등 다양한 멀티미디어 콘텐츠를 전송할 수 있어 고객 참여도를
              높입니다.
            </>
          }
        />

        {/* RCS Templates Section */}
        <section className="mb-20">
          <div className="grid items-center grid-cols-1 gap-12 lg:grid-cols-2">
            <div>
              <div className="mb-6">
                <Badge size="md" color="white" className="bg-[#1972D2]">
                  RCS 서비스
                </Badge>
              </div>
              <h3 className="text-[3.2rem] font-bold mb-6 leading-[1.3]">
                별도의 앱 설치 없이
                <br />
                풍부한 메시지 발송
              </h3>
              <p className="text-[1.6rem] text-gray-6 leading-[1.8] mb-8">
                별도의 메신저 설치 없이 실시간 고객 커뮤니케이션이 가능한 RCS는 3세대
                표준문자입니다.
                <br />
                삼성 갤럭시(안드로이드)를 통해 iOS와 같이 풍부한 소통 및 서비스를 제공하는 고객 맞춤
                반응형 메시지를 발송할 수 있습니다.
              </p>

              {/* Features */}
              <div className="flex flex-wrap gap-3">
                {["텍스트", "이미지", "동영상", "버튼"].map((item, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-[#f0f3ff] text-navy rounded-lg text-[1.4rem] font-medium"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Templates */}
            <div className="bg-[#f8f9ff] rounded-3xl p-8">
              <div className="grid grid-cols-3 gap-4">
                {rcsTemplates.map((template, idx) => (
                  <div key={idx} className="text-center">
                    <div className="mb-3 overflow-hidden bg-white shadow-md rounded-xl">
                      <img
                        src={template.image}
                        alt={template.title}
                        className="w-full h-auto"
                        loading="lazy"
                      />
                    </div>
                    <h5 className="text-[1.3rem] font-semibold leading-tight">{template.title}</h5>
                    <p className="text-[1.2rem] text-gray-5">{template.type}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Service Purpose */}
        <>
          <div className="mb-6">
            <Badge size="md" color="white" className="bg-[#1972D2]">
              서비스 목적
            </Badge>
          </div>
          <section className="py-16 bg-[#f8f9ff] rounded-3xl mb-20 px-8 lg:px-12">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {purposes.map((purpose, idx) => (
                <div key={idx} className="p-8 bg-white shadow-sm rounded-2xl">
                  <span className="material-icons-round text-[4rem] text-navy mb-4">
                    {purpose.icon}
                  </span>
                  <h4 className="text-[1.8rem] font-bold mb-3">{purpose.title}</h4>
                  <p className="text-[1.5rem] text-gray-6 leading-[1.7]">{purpose.description}</p>
                </div>
              ))}
            </div>
          </section>
        </>
        {/* Service Capabilities */}
        <section className="mb-20">
          <div className="mb-6">
            <Badge size="md" color="white" className="bg-[#1972D2]">
              서비스 기능
            </Badge>
          </div>
          <h3 className="text-[2.8rem] font-bold mb-10">RCS가 제공하는 핵심 기능</h3>

          {/* Flow Diagram */}
          <div className="relative mx-auto">
            <div className="relative z-10 grid grid-cols-1 gap-8 md:grid-cols-3">
              {capabilities.map(cap => (
                <div key={cap.step} className="flex flex-col items-center text-center">
                  <div className="w-[70px] h-[70px] rounded-full bg-navy text-white flex items-center justify-center text-[2.4rem] font-bold mb-6 border-4 border-white shadow-lg">
                    {cap.step}
                  </div>
                  <Card>
                    <h4 className="text-[1.8rem] font-semibold mb-3">{cap.title}</h4>
                    <p className="text-[1.4rem] text-gray-6 leading-[1.7]">{cap.description}</p>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 text-center">
          <h3 className="text-[2.8rem] font-bold mb-4">
            차세대 메시징의 새로운 표준
            <br />
            RCS
          </h3>
          <Link
            to="/service/submit"
            className="inline-flex items-center gap-2 mt-6 px-8 py-4 bg-navy text-white rounded-sm text-[1.6rem] font-medium hover:bg-navy/90 transition-colors"
          >
            서비스 신청하기
          </Link>
        </section>
      </section>
    </Container>
  );
};

export default BusinessRcs;
